import req from 'shared/req';

// A element of the Predictions array returned from a PlaceAutocomplete request: https://developers.google.com/maps/documentation/places/web-service/autocomplete#PlaceAutocompletePrediction
export interface GooglePlacesPrediction {
  description: string;
  place_id: string;
  terms: GooglePlacesPredictionTerm[];
}

interface GooglePlacesPredictionTerm {
  value: string;
  offset: number; // char offset from start of prediction description string
}

// The result of a call to googlePlacesClient.PlaceDetails: https://developers.google.com/maps/documentation/places/web-service/details#Place
export interface GooglePlacesAddressDetails {
  components: GooglePlacesAddressComponent[];
  streetNumber: string;
  route: string;
  address: string;
  country: string;
  region: string;
  city: string;
  postal_code: string;
}

interface GooglePlacesAddressComponent {
  long_name: number | string;
  short_name: number | string;
  types: string[];
}

/* 
A service for speaking to Anubis to grab info from the GooglePlaces API. Anubis was necessary instead of a direct request because CapacitorIOS sends requests with the 
capacitor:// origin instead of https, which the Places API does not allow, so we needed a middleman.
*/
class GooglePlacesService {
  getPredictions = async (addressInput: string) =>
    req<GooglePlacesPrediction[]>({
      url: '/v1/address-autocomplete',
      method: 'get',
      data: {
        address: addressInput,
      },
    });

  getAddressDetails = async (placeId: string) =>
    this.formatAddressDetails(
      await req<GooglePlacesAddressComponent[]>({
        url: `/v1/address-details/${placeId}`,
        method: 'get',
      })
    );

  private formatAddressDetails = async (
    rawDetailsResult: GooglePlacesAddressComponent[]
  ): Promise<GooglePlacesAddressDetails> => {
    const components = rawDetailsResult;
    const streetNumber = this.getAddressComponentField(components, 'street_number');
    const route = this.getAddressComponentField(components, 'route');

    return {
      components,
      country: this.getAddressComponentField(components, 'country'),
      streetNumber,
      route,
      city:
        this.getAddressComponentField(components, 'locality', 'long_name') ||
        this.getAddressComponentField(components, 'postal_town', 'long_name'),
      region: this.getAddressComponentField(components, 'administrative_area_level_1'),
      postal_code: this.getAddressComponentField(components, 'postal_code'),
      address: streetNumber ? `${streetNumber} ${route}` : route,
    };
  };

  private getAddressComponentField = (
    components: GooglePlacesAddressComponent[],
    field: string,
    name_type = 'short_name'
  ) => {
    const component = components.find((entry) => entry.types.indexOf(field) !== -1);
    if (component) {
      return component[name_type];
    }
  };
}

export default new GooglePlacesService();
